import React, { useEffect, useState } from 'react';
import { Card, Form, InputNumber, Select, Button, message, Space, Row, Col, Spin } from 'antd';
import { SaveOutlined, ThunderboltOutlined } from '@ant-design/icons';
import moment from 'moment-jalaali';
import { attendanceSettingsApi, show422Error, ValidationError } from '../../../utils/api';

const { Option } = Select;

const CURRENT_JY = Number(moment().format('jYYYY'));
const YEAR_OPTIONS = [CURRENT_JY - 1, CURRENT_JY, CURRENT_JY + 1, CURRENT_JY + 2];

const MonthlyLaborHoursTab = () => {
  const [form] = Form.useForm();
  const [year, setYear] = useState(CURRENT_JY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [presetLoading, setPresetLoading] = useState(false);
  const [months, setMonths] = useState([]);

  const applyMonthsToForm = (list = []) => {
    const values = {};
    list.forEach((m) => {
      values[`month_${m.month}`] = m.hours != null ? Number(m.hours) : null;
    });
    form.setFieldsValue(values);
    setMonths(list);
  };

  const loadYear = async (y) => {
    setLoading(true);
    try {
      const res = await attendanceSettingsApi.getMonthlyHours(y);
      const data = res?.data || res || {};
      applyMonthsToForm(data.months || []);
      if (data.year) setYear(Number(data.year));
    } catch (error) {
      console.error(error);
      message.error('خطا در دریافت ساعات موظفی ماهانه');
      applyMonthsToForm([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadYear(year);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payloadMonths = (months.length
        ? months
        : Array.from({ length: 12 }, (_, i) => ({ month: i + 1 }))
      ).map((m) => ({
        month: m.month,
        hours: values[`month_${m.month}`] != null ? Number(values[`month_${m.month}`]) : null,
      })).filter((m) => m.hours != null && !Number.isNaN(m.hours));

      const res = await attendanceSettingsApi.saveMonthlyHours({
        year: Number(year),
        months: payloadMonths,
      });
      const data = res?.data || res || {};
      applyMonthsToForm(data.months || months);
      message.success('ساعات موظفی ماهانه ذخیره شد');
    } catch (error) {
      if (error instanceof ValidationError || (error.error && error.error.details)) {
        show422Error(error, form);
      } else if (!error.errorFields) {
        message.error(error?.message || 'خطا در ذخیره ساعات موظفی');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleLoadPreset = async () => {
    setPresetLoading(true);
    try {
      const res = await attendanceSettingsApi.loadLaborPreset(year);
      const data = res?.data || res || {};
      applyMonthsToForm(data.months || []);
      message.success('جدول مصوب وزارت کار بارگذاری شد — در صورت نیاز ویرایش و ذخیره کنید');
    } catch (error) {
      if (error instanceof ValidationError || (error.error && error.error.details)) {
        show422Error(error, form);
      } else {
        message.error(error?.message || 'خطا در بارگذاری جدول اداره کار');
      }
    } finally {
      setPresetLoading(false);
    }
  };

  const displayMonths = months.length
    ? months
    : [
        { month: 1, month_name: 'فروردین' },
        { month: 2, month_name: 'اردیبهشت' },
        { month: 3, month_name: 'خرداد' },
        { month: 4, month_name: 'تیر' },
        { month: 5, month_name: 'مرداد' },
        { month: 6, month_name: 'شهریور' },
        { month: 7, month_name: 'مهر' },
        { month: 8, month_name: 'آبان' },
        { month: 9, month_name: 'آذر' },
        { month: 10, month_name: 'دی' },
        { month: 11, month_name: 'بهمن' },
        { month: 12, month_name: 'اسفند' },
      ];

  return (
    <Card className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700" style={{ fontFamily: 'CustomFont' }}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 m-0" style={{ fontFamily: 'CustomFont' }}>
            ساعات موظفی ماهانه (اداره کار)
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 m-0 mt-1" style={{ fontFamily: 'CustomFont' }}>
            ساعات مصوب ۱۲ ماه سال شمسی را وارد کنید؛ در خلاصه حضور هر پرسنل به‌عنوان «موظفی» استفاده می‌شود.
          </p>
        </div>
        <Space wrap>
          <Select
            value={year}
            onChange={setYear}
            style={{ width: 120, fontFamily: 'CustomFont' }}
          >
            {YEAR_OPTIONS.map((y) => (
              <Option key={y} value={y}>{y}</Option>
            ))}
          </Select>
          <Button
            icon={<ThunderboltOutlined />}
            loading={presetLoading}
            onClick={handleLoadPreset}
            style={{ fontFamily: 'CustomFont' }}
          >
            بارگذاری جدول وزارت کار
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={handleSave}
            style={{ fontFamily: 'CustomFont' }}
          >
            ذخیره تغییرات
          </Button>
        </Space>
      </div>

      {loading ? (
        <div className="py-12 text-center"><Spin /></div>
      ) : (
        <Form form={form} layout="vertical" style={{ fontFamily: 'CustomFont' }}>
          <Row gutter={[16, 8]}>
            {displayMonths.map((m) => (
              <Col xs={24} sm={12} md={8} lg={6} key={m.month}>
                <Form.Item
                  name={`month_${m.month}`}
                  label={
                    <span style={{ fontFamily: 'CustomFont' }}>
                      {m.month_name || `ماه ${m.month}`}
                      {m.is_set === false && (
                        <span className="text-amber-600 text-xs mr-2"> (تنظیم نشده)</span>
                      )}
                    </span>
                  }
                >
                  <InputNumber
                    min={0}
                    max={744}
                    step={1}
                    style={{ width: '100%', fontFamily: 'CustomFont' }}
                    placeholder="مثلاً ۱۷۶"
                    addonAfter="ساعت"
                  />
                </Form.Item>
              </Col>
            ))}
          </Row>
        </Form>
      )}
    </Card>
  );
};

export default MonthlyLaborHoursTab;
